import { ReactNode, useRef, useState } from 'react'

import { useForm } from 'react-hook-form'
import { useQuery } from 'urql'

import { ViewerDocument } from 'graphql/schema/user'
import logger from 'lib/utils/logger'
import { toastError, toasty } from 'store/toasts.state'

import Box from 'components/atomic/atoms/box'
import Button from 'components/atomic/atoms/button'
import Text from 'components/atomic/atoms/text'
import TextArea from 'components/atomic/atoms/textarea'
import Modal from 'components/atomic/molecules/modal'

const organizationId = process.env.NEXT_PUBLIC_PRODUCTLANE_ORGANIZATION_ID

const Feedback = ({ trigger }: { trigger: ReactNode }) => {
  const [isLoading, setLoading] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const [{ data, fetching }] = useQuery({
    query: ViewerDocument,
    requestPolicy: 'cache-first'
  })

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitted }
  } = useForm({
    defaultValues: {
      text: ''
    }
  })

  const onSubmit = handleSubmit(async ({ text }) => {
    const email = data?.viewer?.email
    setLoading(true)
    try {
      const response = await fetch(
        'https://productlane.com/api/rpc/submitFeedback',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            params: {
              email,
              text,
              organizationId,
              painLevel: 'UNKNOWN'
            }
          })
        }
      )
      const data = await response.json()
      if (data.error) {
        throw new Error(data.error)
      }
      triggerRef.current?.click()
      return toasty.success({
        title: 'Thank you! 👌',
        text: 'We have received your feedback.'
      })
    } catch (error) {
      toastError()
      logger.error(error)
    } finally {
      setLoading(false)
    }
  })

  const hasMessage = watch('text')

  return (
    <Modal.Root onOpenChange={() => reset()}>
      <Modal.Trigger ref={triggerRef} asChild>
        {trigger}
      </Modal.Trigger>
      <Modal.Content>
        <Box as="form" onSubmit={onSubmit} data-testid="feedback-form">
          <Modal.Title>Feedback</Modal.Title>
          <Box css={{ mt: '$10', spaceY: 4 }}>
            <Modal.Description>
              We would love to hear your thoughts on how we can improve.
            </Modal.Description>
            <TextArea
              placeholder="Write your message here"
              {...register('text', {
                required: {
                  value: true,
                  message: 'Please enter a message'
                },
                pattern: {
                  value: /[a-zA-Z]+\s+[a-zA-Z]+/g,
                  message: 'Enter at least 2 words'
                }
              })}
            />
          </Box>
          {isSubmitted && !!errors.text && (
            <Text size="xs" color="textError">
              {errors.text.message}
            </Text>
          )}
          <Box css={{ mt: '$6', textAlign: 'right' }}>
            <Button
              type="submit"
              isLoading={isLoading || fetching}
              disabled={!hasMessage || fetching}
            >
              Submit
            </Button>
          </Box>
        </Box>
      </Modal.Content>
    </Modal.Root>
  )
}

export default Feedback
